import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, EyeOff, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import Logo from "../components/app/Logo";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

function Field({ label, value, onChange, placeholder, type = "text", testId, autoCapitalize, maxLength }) {
  return (
    <div>
      <label className="label-up">{label}</label>
      <input data-testid={testId} className="input-field" type={type} placeholder={placeholder} value={value}
        onChange={(e) => onChange(e.target.value)} autoCapitalize={autoCapitalize} maxLength={maxLength} />
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [agency, setAgency] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const code = params.get("code");
    if (code) setInviteCode(code.toUpperCase());
  }, [params]);

  useEffect(() => {
    const code = inviteCode.trim().toUpperCase();
    if (!code || code.length < 4) { setInviteInfo(null); setInviteError(""); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const info = await api(`/auth/invite-lookup?code=${encodeURIComponent(code)}`, { auth: false });
        if (!cancel) { setInviteInfo(info); setInviteError(""); }
      } catch {
        if (!cancel) { setInviteInfo(null); setInviteError("Invalid invite code"); }
      }
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [inviteCode]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name || !email || !password || !phone) { setError("Please fill out name, phone, email, and password."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setError(""); setLoading(true);
    try {
      await signUp({
        name: name.trim(), email: email.trim(), password,
        agency: agency.trim(), phone: phone.trim(),
        invite_code: inviteCode.trim().toUpperCase() || undefined,
      });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-6 py-10">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-10 h-10 grid place-items-center -ml-2 hover:bg-[#F5F5F5] rounded-lg">
          <ArrowLeft size={20} />
        </button>
        <div className="mt-6"><Logo /></div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-8">Become a Partner</h1>
        <p className="text-[15px] mt-2" style={{ color: "#5A5A5A" }}>Create your account to start submitting referrals.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <Field label="Full Name" value={name} onChange={setName} placeholder="Jane Smith" testId="reg-name" />
          <Field label="Agency / Uplink (optional)" value={agency} onChange={setAgency} placeholder="Acme Insurance Group" testId="reg-agency" />
          <Field label="Phone" value={phone} onChange={setPhone} placeholder="(555) 123-4567" type="tel" testId="reg-phone" />
          <Field label="Email" value={email} onChange={setEmail} placeholder="you@agency.com" type="email" testId="reg-email" />

          <div>
            <label className="label-up">Invite Code (optional)</label>
            <input data-testid="reg-invite-code" className="input-field" placeholder="e.g. AB12CD34" value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} maxLength={12} />
            {inviteInfo ? (
              <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-[#E8F5E9]">
                <CheckCircle2 size={14} color="#1B5E20" className="mt-0.5" />
                <span className="text-xs text-[#1B5E20]">
                  Joining {inviteInfo.role === "agent" ? "agent" : "admin"} <strong>{inviteInfo.name}</strong>
                  {inviteInfo.agency ? ` (${inviteInfo.agency})` : ""}
                </span>
              </div>
            ) : inviteError ? (
              <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg status-declined">
                <AlertCircle size={14} className="mt-0.5" /><span className="text-xs">{inviteError}</span>
              </div>
            ) : (
              <p className="text-xs italic mt-2" style={{ color: "#8A8A8A" }}>If you were invited by an agent, paste their code here.</p>
            )}
          </div>

          <div>
            <label className="label-up">Password</label>
            <div className="relative">
              <input data-testid="reg-password" className="input-field pr-12" type={showPwd ? "text" : "password"} placeholder="At least 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} />
              <button data-testid="toggle-pwd-btn" type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5">
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <div data-testid="reg-error" className="flex items-start gap-2 p-3 rounded-xl status-declined">
              <AlertCircle size={16} className="mt-0.5" /><span className="text-sm">{error}</span>
            </div>
          )}

          <button data-testid="reg-submit-btn" type="submit" disabled={loading} className="btn-primary w-full !py-3.5">
            {loading ? <Loader2 className="animate-spin" size={18} /> : (<>Create Account <ArrowRight size={18} /></>)}
          </button>
        </form>

        <Link data-testid="go-login-btn" to="/login" className="block text-center mt-6 text-sm" style={{ color: "#5A5A5A" }}>
          Already have an account? <span className="font-extrabold" style={{ color: "#D62828" }}>Sign In</span>
        </Link>
      </div>
    </div>
  );
}
